(function () {
  'use strict'

  function isMobile() {
    const width = document.documentElement.clientWidth || document.body.clientWidth || window.innerWidth
    const breakpoint = window.theme && window.theme.sizes ? window.theme.sizes.small : 750
    return width < breakpoint
  }

  const SELECTOR_BUTTON_ARROW = '[data-button-arrow]'
  const SELECTOR_DEFERRED_MEDIA_BUTTON = '[data-deferred-media-button]'
  const SELECTOR_FOCUSABLE = 'model-viewer, video, iframe, button, [href], input, [tabindex]'
  const SELECTOR_MEDIA_ITEM = '[data-image-id]'
  const SELECTOR_MEDIA_LIST = '[data-product-media-list]'
  const SELECTOR_SECTION = '[data-section-type]'

  const CLASS_ARROWS = 'slider__arrows'
  const CLASS_DRAGGING = 'is-dragging'
  const CLASS_FOCUSED = 'is-focused'
  const CLASS_ACTIVE = 'media--active'
  const CLASS_HIDDEN = 'media--hidden'
  const CLASS_HIDING = 'media--hiding'

  const ATTR_ACTIVE_MEDIA = 'data-active-media'
  const ATTR_BUTTON_PREV = 'data-button-prev'
  const ATTR_BUTTON_NEXT = 'data-button-next'
  const ATTR_IMAGE_ID = 'data-image-id'
  const ATTR_MEDIA_ID = 'data-media-id'
  const ATTR_TYPE = 'data-type'
  const ATTR_FADER_DESKTOP = 'data-fader-desktop'
  const ATTR_FADER_MOBILE = 'data-fader-mobile'

  if (customElements.get('product-images')) return

  customElements.define(
    'product-images',
    class ProductImages extends HTMLElement {
      constructor() {
        super()

        this.initialized = false
        this.buttons = []
        this.isDown = false
        this.startX = 0
        this.startY = 0
        this.scrollLeft = 0

        this.container = this.closest(SELECTOR_SECTION)
        this.productMediaItems = this.querySelectorAll(SELECTOR_MEDIA_ITEM)
        this.productMediaList = this.querySelector(SELECTOR_MEDIA_LIST)

        this.handleMouseDown = this.handleMouseDown.bind(this)
        this.handleMouseLeave = this.handleMouseLeave.bind(this)
        this.handleMouseUp = this.handleMouseUp.bind(this)
        this.handleMouseMove = this.handleMouseMove.bind(this)
        this.handleKeyUp = this.handleKeyUp.bind(this)
        this.setHeight = this.setHeight.bind(this)
        this.toggleEvents = this.toggleEvents.bind(this)
        this.selectMediaEvent = (event) => this.showMediaOnVariantSelect(event)
      }

      connectedCallback() {
        if (!this.productMediaList || this.productMediaItems.length <= 1) return

        this.productMediaObserver()
        this.toggleEvents()
        this.listen()
        this.setHeight()
      }

      disconnectedCallback() {
        this.unlisten()
        this.unbindEventListeners()
      }

      listen() {
        document.addEventListener('theme:resize:width', this.toggleEvents)
        document.addEventListener('theme:resize:width', this.setHeight)
        this.addEventListener('theme:media:select', this.selectMediaEvent)
      }

      unlisten() {
        document.removeEventListener('theme:resize:width', this.toggleEvents)
        document.removeEventListener('theme:resize:width', this.setHeight)
        this.removeEventListener('theme:media:select', this.selectMediaEvent)
      }

      toggleEvents() {
        const mobile = isMobile()

        if ((mobile && this.hasAttribute(ATTR_FADER_MOBILE)) || (!mobile && this.hasAttribute(ATTR_FADER_DESKTOP))) {
          this.bindEventListeners()
        } else {
          this.unbindEventListeners()
        }
      }

      bindEventListeners() {
        if (this.initialized || !this.productMediaList) return

        this.productMediaList.addEventListener('mousedown', this.handleMouseDown)
        this.productMediaList.addEventListener('mouseleave', this.handleMouseLeave)
        this.productMediaList.addEventListener('mouseup', this.handleMouseUp)
        this.productMediaList.addEventListener('mousemove', this.handleMouseMove)
        this.productMediaList.addEventListener('touchstart', this.handleMouseDown, { passive: true })
        this.productMediaList.addEventListener('touchend', this.handleMouseUp, { passive: true })
        this.productMediaList.addEventListener('touchmove', this.handleMouseMove, { passive: true })
        this.productMediaList.addEventListener('keyup', this.handleKeyUp)

        this.initArrows()
        this.resetScrollPosition()
        this.initialized = true
      }

      unbindEventListeners() {
        if (!this.initialized || !this.productMediaList) return

        this.productMediaList.removeEventListener('mousedown', this.handleMouseDown)
        this.productMediaList.removeEventListener('mouseleave', this.handleMouseLeave)
        this.productMediaList.removeEventListener('mouseup', this.handleMouseUp)
        this.productMediaList.removeEventListener('mousemove', this.handleMouseMove)
        this.productMediaList.removeEventListener('touchstart', this.handleMouseDown)
        this.productMediaList.removeEventListener('touchend', this.handleMouseUp)
        this.productMediaList.removeEventListener('touchmove', this.handleMouseMove)
        this.productMediaList.removeEventListener('keyup', this.handleKeyUp)

        this.removeArrows()
        this.initialized = false
      }

      pointerX(event) {
        return event.pageX || event.changedTouches?.[0]?.screenX || 0
      }

      pointerY(event) {
        return event.pageY || event.changedTouches?.[0]?.screenY || 0
      }

      handleMouseDown(event) {
        if (isMobile()) {
          this.isDown = false
          this.classList.remove(CLASS_DRAGGING)
          return
        }

        this.isDown = true
        this.startX = this.pointerX(event) - this.offsetLeft
        this.startY = this.pointerY(event) - this.offsetTop
      }

      handleMouseLeave() {
        if (this.isDown) {
          this.isDown = false
        }
      }

      handleMouseUp(event) {
        if (!this.isDown || isMobile()) {
          this.isDown = false
          this.classList.remove(CLASS_DRAGGING)
          return
        }

        const x = this.pointerX(event) - this.offsetLeft
        const y = this.pointerY(event) - this.offsetTop
        const deltaX = x - this.startX
        const deltaY = y - this.startY
        const direction = deltaX > 0 ? 1 : -1
        const currentMedia = this.getCurrentMedia()
        const currentIsImage = currentMedia?.hasAttribute(ATTR_TYPE) && currentMedia.getAttribute(ATTR_TYPE) === 'image'

        if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY) && currentIsImage) {
          direction < 0 ? this.showNextImage() : this.showPreviousImage()
        }

        this.isDown = false

        requestAnimationFrame(() => {
          this.classList.remove(CLASS_DRAGGING)
        })
      }

      handleMouseMove() {
        if (isMobile()) {
          this.isDown = false
          this.classList.remove(CLASS_DRAGGING)
          return
        }

        if (this.isDown) {
          this.classList.add(CLASS_DRAGGING)
        }
      }

      handleKeyUp(event) {
        if (event.code === 'ArrowLeft') this.showPreviousImage()
        if (event.code === 'ArrowRight') this.showNextImage()
      }

      handleArrowsClickEvent() {
        this.querySelectorAll(SELECTOR_BUTTON_ARROW).forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault()

            if (button.hasAttribute(ATTR_BUTTON_PREV)) this.showPreviousImage()
            if (button.hasAttribute(ATTR_BUTTON_NEXT)) this.showNextImage()
          })
        })
      }

      resetScrollPosition() {
        if (this.productMediaList.scrollLeft !== 0) {
          this.productMediaList.scrollLeft = 0
        }
      }

      initArrows() {
        if (!this.buttons.length) {
          const arrows = window.theme && window.theme.sliderArrows
          if (!arrows) return

          const arrowsEl = document.createElement('div')
          arrowsEl.classList.add(CLASS_ARROWS)
          arrowsEl.innerHTML = arrows.prev + arrows.next

          this.productMediaList.append(arrowsEl)

          this.buttons = this.querySelectorAll(SELECTOR_BUTTON_ARROW)
          this.buttonPrev = this.querySelector(`[${ATTR_BUTTON_PREV}]`)
          this.buttonNext = this.querySelector(`[${ATTR_BUTTON_NEXT}]`)
        }

        this.handleArrowsClickEvent()
        this.preloadImageOnArrowHover()
      }

      removeArrows() {
        this.querySelector(`.${CLASS_ARROWS}`)?.remove()
        this.buttons = []
        this.buttonPrev = null
        this.buttonNext = null
      }

      preloadImageOnArrowHover() {
        this.buttonPrev?.addEventListener('mouseover', () => {
          this.preloadImage(this.getPreviousMediaId())
        })

        this.buttonNext?.addEventListener('mouseover', () => {
          this.preloadImage(this.getNextMediaId())
        })
      }

      preloadImage(mediaId) {
        this.querySelector(`[${ATTR_MEDIA_ID}="${mediaId}"] img`)?.setAttribute('loading', 'eager')
      }

      showMediaOnVariantSelect(event) {
        const mediaId = event.detail.id
        this.setActiveMedia(mediaId)
      }

      getCurrentMedia() {
        return this.querySelector(`${SELECTOR_MEDIA_ITEM}.${CLASS_ACTIVE}`)
      }

      getNextMediaId() {
        const currentMedia = this.getCurrentMedia()
        const nextMedia = currentMedia?.nextElementSibling

        return (nextMedia?.hasAttribute(ATTR_IMAGE_ID) ? nextMedia : this.querySelector(SELECTOR_MEDIA_ITEM))?.getAttribute(
          ATTR_MEDIA_ID
        )
      }

      getPreviousMediaId() {
        const currentMedia = this.getCurrentMedia()
        const lastIndex = this.productMediaItems.length - 1

        return (currentMedia?.previousElementSibling || this.productMediaItems[lastIndex])?.getAttribute(ATTR_MEDIA_ID)
      }

      showNextImage() {
        this.selectMedia(this.getNextMediaId())
      }

      showPreviousImage() {
        this.selectMedia(this.getPreviousMediaId())
      }

      selectMedia(mediaId) {
        this.dispatchEvent(
          new CustomEvent('theme:media:select', {
            detail: { id: mediaId },
          })
        )
      }

      setActiveMedia(mediaId) {
        if (!mediaId) return

        const activeMedia = this.querySelector(`${SELECTOR_MEDIA_ITEM}.${CLASS_ACTIVE}`)
        const nextMedia = this.querySelector(`[${ATTR_MEDIA_ID}="${mediaId}"]`)

        if (!nextMedia) return

        const focusable = nextMedia.querySelector(SELECTOR_FOCUSABLE)
        const deferredMedia = nextMedia.querySelector('deferred-media')

        this.setAttribute(ATTR_ACTIVE_MEDIA, mediaId)

        activeMedia?.classList.add(CLASS_HIDING)
        activeMedia?.classList.remove(CLASS_ACTIVE)

        nextMedia.classList.remove(CLASS_HIDING, CLASS_HIDDEN)
        nextMedia.classList.add(CLASS_ACTIVE)

        if (deferredMedia && deferredMedia.getAttribute('loaded') !== 'true') {
          nextMedia.querySelector(SELECTOR_DEFERRED_MEDIA_BUTTON)?.dispatchEvent(
            new Event('click', {
              bubbles: false,
            })
          )
        }

        requestAnimationFrame(() => {
          this.setHeight()

          if (document.body.classList.contains(CLASS_FOCUSED)) {
            focusable?.focus()
          }
        })
      }

      setHeight() {
        const height = this.querySelector(`${SELECTOR_MEDIA_ITEM}.${CLASS_ACTIVE}`)?.offsetHeight || this.productMediaItems[0]?.offsetHeight

        if (height) {
          this.style.setProperty('--height', `${height}px`)
        }
      }

      productMediaObserver() {
        this.productMediaItems.forEach((mediaItem) => {
          mediaItem.addEventListener('transitionend', (event) => {
            if (event.target === mediaItem && mediaItem.classList.contains(CLASS_HIDING)) {
              mediaItem.classList.remove(CLASS_HIDING)
              mediaItem.classList.add(CLASS_HIDDEN)
            }
          })

          mediaItem.addEventListener('transitioncancel', (event) => {
            if (event.target === mediaItem && mediaItem.classList.contains(CLASS_HIDING)) {
              mediaItem.classList.remove(CLASS_HIDING)
              mediaItem.classList.add(CLASS_HIDDEN)
            }
          })
        })
      }
    }
  )
})()